/**
 * Radix's Dialog/AlertDialog/DropdownMenu/Popover "modal" layers lock
 * background interaction by setting `document.body.style.pointerEvents =
 * "none"` while open, and are supposed to restore it when the layer
 * unmounts. When one such layer is opened from inside another (this app's
 * DropdownMenuItem -> `setTimeout(() => setDialogOpen(true), 0)` pattern,
 * used throughout InvoiceCard.tsx to avoid the dropdown closing underneath
 * the dialog it opens), or when a dialog's own confirm action triggers a
 * mutation whose success removes the row that owns it, the lock/unlock
 * cycles can interleave and the restore step never runs. The result:
 * `body.style.pointerEvents` stays "none" forever, silently swallowing
 * every click app-wide until a full page reload.
 *
 * There is no single reliable moment to check from — the exit animation
 * (200ms), the actual DOM removal of the dialog's content, and a
 * mutation's list-refetch-driven unmount of the owning row can each finish
 * at different times depending on the flow. Instead of guessing one delay,
 * this installs one app-wide `MutationObserver` on `<body>`'s `style`
 * attribute: whenever `pointer-events: none` is applied, it polls (short,
 * bounded interval) until either the lock clears itself or no Radix layer
 * is actually `data-state="open"` anywhere in the document, at which point
 * the lock is provably stray (Radix never leaves a *legitimate* lock
 * active with nothing open) and is cleared. A real, currently-open modal
 * always has at least one `data-state="open"` element, so this can never
 * yank the lock out from under an actual open dialog/dropdown/popover.
 */
const POLL_INTERVAL_MS = 200;
const MAX_POLL_ATTEMPTS = 20; // ~4s ceiling, generous vs. any real close/unmount race

// Only actual modal/menu content can own the body-level lock. A generic
// `[data-state="open"]` selector also matches unrelated persistent widgets
// (tabs, collapsibles, switches, etc.), causing a stray lock to survive
// forever after the real dialog closes. Radix assigns these roles to the
// content element for every lock-owning layer used by this app.
const OPEN_LOCK_OWNER_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
].join(',');

let installed = false;
let pollHandle: number | undefined;

function pollUntilClear(attempt: number): void {
  if (document.body.style.pointerEvents !== "none") {
    pollHandle = undefined;
    return; // already resolved (naturally or by us)
  }
  const anyRadixLayerOpen = document.querySelector(OPEN_LOCK_OWNER_SELECTOR);
  if (!anyRadixLayerOpen) {
    document.body.style.pointerEvents = "";
    pollHandle = undefined;
    return;
  }
  if (attempt >= MAX_POLL_ATTEMPTS) {
    pollHandle = undefined;
    return;
  }
  pollHandle = window.setTimeout(() => pollUntilClear(attempt + 1), POLL_INTERVAL_MS);
}

export function installRadixBodyLockGuard(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;

  const observer = new MutationObserver(() => {
    if (document.body.style.pointerEvents !== "none") return;
    if (pollHandle !== undefined) return; // a poll loop is already chasing this lock
    pollHandle = window.setTimeout(() => pollUntilClear(0), POLL_INTERVAL_MS);
  });

  observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
}
